import React, { useId } from 'react'

/**
 * Flat stand-in for BrandPrimitives, shown when WebGL is unavailable, the
 * viewport is small, or motion is unwanted.
 *
 * Same three shapes and the same three logo colours, drawn with gradients and
 * a contact shadow so it reads as lit geometry rather than clip art. Layouts
 * mirror the 3D variants so a page does not change composition depending on
 * which one renders.
 */

const LAYOUTS = {
  trio: [
    { shape: 'cube', cx: 110, cy: 150, r: 52, tone: 'gold' },
    { shape: 'cone', cx: 250, cy: 150, r: 46, tone: 'cream' },
    { shape: 'sphere', cx: 185, cy: 232, r: 38, tone: 'pewter' }
  ],
  column: [
    { shape: 'sphere', cx: 180, cy: 78, r: 36, tone: 'gold' },
    { shape: 'cone', cx: 180, cy: 168, r: 48, tone: 'cream' },
    { shape: 'cube', cx: 180, cy: 262, r: 56, tone: 'pewter' }
  ],
  row: [
    { shape: 'cone', cx: 88, cy: 170, r: 44, tone: 'gold' },
    { shape: 'sphere', cx: 182, cy: 170, r: 40, tone: 'cream' },
    { shape: 'cube', cx: 282, cy: 170, r: 50, tone: 'pewter' }
  ],
  cluster: [
    { shape: 'cube', cx: 128, cy: 128, r: 60, tone: 'pewter' },
    { shape: 'sphere', cx: 258, cy: 148, r: 42, tone: 'gold' },
    { shape: 'cone', cx: 178, cy: 246, r: 46, tone: 'cream' }
  ]
}

// Sampled from the logo.
const TONES = {
  gold: { light: '#ffe9a8', mid: '#d8a848', dark: '#8a6416' },
  cream: { light: '#ffffff', mid: '#f0f0d8', dark: '#9c9c86' },
  pewter: { light: '#b4b4b4', mid: '#6e6e6e', dark: '#2e2e2e' }
}

function Shape({ shape, cx, cy, r, fill, stroke }) {
  if (shape === 'cube') {
    // Isometric cube: three faces, so it reads as solid.
    const h = r * 0.55
    const top = `${cx},${cy - r * 0.85} ${cx + r * 0.86},${cy - r * 0.35} ${cx},${cy + r * 0.15} ${cx - r * 0.86},${cy - r * 0.35}`
    const left = `${cx - r * 0.86},${cy - r * 0.35} ${cx},${cy + r * 0.15} ${cx},${cy + r * 0.15 + h} ${cx - r * 0.86},${cy - r * 0.35 + h}`
    const right = `${cx + r * 0.86},${cy - r * 0.35} ${cx},${cy + r * 0.15} ${cx},${cy + r * 0.15 + h} ${cx + r * 0.86},${cy - r * 0.35 + h}`
    return (
      <g>
        <polygon points={right} fill={fill} opacity="0.55" />
        <polygon points={left} fill={fill} opacity="0.78" />
        <polygon points={top} fill={fill} />
        <polyline points={top} fill="none" stroke={stroke} strokeWidth="1" opacity="0.7" />
      </g>
    )
  }
  if (shape === 'cone') {
    const h = r * 1.7
    return (
      <g>
        <polygon
          points={`${cx},${cy - h / 2} ${cx + r * 0.72},${cy + h / 2} ${cx - r * 0.72},${cy + h / 2}`}
          fill={fill}
        />
        <ellipse cx={cx} cy={cy + h / 2} rx={r * 0.72} ry={r * 0.2} fill={fill} opacity="0.6" />
        <line
          x1={cx} y1={cy - h / 2} x2={cx - r * 0.72} y2={cy + h / 2}
          stroke={stroke} strokeWidth="1" opacity="0.65"
        />
      </g>
    )
  }
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={fill} />
      <ellipse cx={cx - r * 0.3} cy={cy - r * 0.34} rx={r * 0.34} ry={r * 0.22} fill={stroke} opacity="0.35" />
    </g>
  )
}

export default function PrimitivesFallback({ variant = 'trio', className = '' }) {
  const uid = useId().replace(/:/g, '')
  const items = LAYOUTS[variant] || LAYOUTS.trio

  return (
    <svg viewBox="0 0 360 340" className={className} role="img" aria-label="Arrangement of a cube, a cone and a sphere in the brand palette.">
      <defs>
        {Object.entries(TONES).map(([name, t]) => (
          <radialGradient key={name} id={`${uid}-${name}`} cx="34%" cy="28%" r="76%">
            <stop offset="0%" stopColor={t.light} />
            <stop offset="55%" stopColor={t.mid} />
            <stop offset="100%" stopColor={t.dark} />
          </radialGradient>
        ))}
        <radialGradient id={`${uid}-glow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffd118" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#ffd118" stopOpacity="0" />
        </radialGradient>
      </defs>

      <ellipse cx="180" cy="170" rx="175" ry="150" fill={`url(#${uid}-glow)`} />

      {/* Contact shadows first, so the shapes sit on something. */}
      {items.map((it, i) => (
        <ellipse
          key={`sh-${i}`}
          cx={it.cx}
          cy={it.cy + it.r * 1.05}
          rx={it.r * 0.95}
          ry={it.r * 0.22}
          fill="rgba(0,0,0,0.5)"
        />
      ))}

      {items.map((it, i) => (
        <Shape
          key={i}
          {...it}
          fill={`url(#${uid}-${it.tone})`}
          stroke={TONES[it.tone].light}
        />
      ))}
    </svg>
  )
}
